import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { editFirmwareInitialState, EditFirmwareContext, TTypeOfModalWindow } from './editFirmwareContext';
import { editFirmwareReducer } from './editFirmwareReducer';
import { EditFirmwareActionTypes } from './editFirmwareActions';
import { IFileData, ICreateUpdateFirmware, IGetFirmwareInfo, TFirmwareType } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getFirmwareInfo } from 'backend/firmware';

const EditFirmwareState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editFirmwareReducer, editFirmwareInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const firmwareID = params.get('firmwareID');

	const { response, loading, refetch } = useFetchData<IGetFirmwareInfo, string>(
		firmwareID ? getFirmwareInfo : null,
		firmwareID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditFirmwareActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditFirmwareActionTypes.SET_ALL_DATA,
				payload: {
					id: response.data.id,
					typeSelectedFirmware: response.data.type,
					nameOfFirmware: response.data.name,
					versionOfFirmware: response.data.version,
					description: response.data.description,
					fileData: {
						fileId: '',
						fileHash: null,
						fileName: response.data.type === 1 ? 'firmware.zip' : 'firmware.bin',
					},
				},
			});
			dispatch({
				type: EditFirmwareActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						typeSelectedFirmware: response.data.type,
						nameOfFirmware: response.data.name,
						versionOfFirmware: response.data.version,
						description: response.data.description,
						fileData: {
							fileId: '',
							fileHash: null,
							fileName: response.data.type === 1 ? 'firmware.zip' : 'firmware.bin',
						},
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditFirmwareActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const setTypeSelectedFirmware = useCallback((value: TFirmwareType) => {
		dispatch({
			type: EditFirmwareActionTypes.SET_TYPE_SELECTED_FIRMWARE,
			payload: {
				typeSelectedFirmware: value,
			},
		});
	}, []);

	const changeNameOfFirmware = useCallback((value: string) => {
		dispatch({
			type: EditFirmwareActionTypes.CHANGE_NAME_OF_FIRMWARE,
			payload: {
				nameOfFirmware: value,
			},
		});
	}, []);

	const changeVersionOfFirmware = useCallback((value: string) => {
		dispatch({
			type: EditFirmwareActionTypes.CHANGE_VERSION_OF_FIRMWARE,
			payload: {
				versionOfFirmware: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditFirmwareActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: EditFirmwareActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: ICreateUpdateFirmware | null) => {
		dispatch({
			type: EditFirmwareActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditFirmwareActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditFirmwareActionTypes.RESET_FIELDS }), []);

	return (
		<EditFirmwareContext.Provider
			value={{
				refetchData,
				setTypeSelectedFirmware,
				changeNameOfFirmware,
				changeVersionOfFirmware,
				changeDescription,
				changeFileData,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				id: state.id,
				typeSelectedFirmware: state.typeSelectedFirmware,
				nameOfFirmware: state.nameOfFirmware,
				versionOfFirmware: state.versionOfFirmware,
				description: state.description,
				fileData: state.fileData,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditFirmwareContext.Provider>
	);
};

export default EditFirmwareState;
